import useFollowFuturesAsSpot from "@shared/features/bots/BotForm/common/hooks/useFollowFuturesAsSpot";
import styled, { css, useTheme } from "styled-components";
import { formPanel, panelTabs } from "features/bots/BotForm/NewLook/styles";
import NewLookBotFormLayout from "features/bots/BotForm/NewLook/NewLookBotFormLayout";
import NewLookMobileBotForm from "features/bots/BotForm/NewLook/NewLookMobileBotForm";
import { useFillHeight } from "features/bots/BotForm/NewLook/useFillHeight";
import PracticeBotPreset, {
  PracticePreset,
} from "features/onboarding/guided/PracticeBotPreset";
import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import General from "features/bots/BotForm/General/General";
import Entries from "features/bots/BotForm/Entries/Entries";
import Stop from "features/bots/BotForm/Stop";
import React, { useContext, useEffect, useMemo } from "react";
import TakeProfits from "features/bots/BotForm/TakeProfits";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import Tabs from "common/Tabs/Tabs";
import Text from "common/Typography/Text";
import Summary from "features/bots/BotForm/Summary";
import Advanced from "features/bots/BotForm/Advanced/Advanced.tsx";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import {
  DcaBotCreateeditOrigin,
  DcaBotCreateeditOriginValueType,
} from "analytics/events";
import useMedia from "utils/hooks/useMedia";
import { typography } from "@shared/common/Themes/typography";
import {
  BotFormType,
  ChartTabs,
  Steps,
} from "@shared/features/bots/BotForm/common/types";
import BotFormProvider from "@shared/features/bots/BotForm/common/BotFormProvider";
import useBotForm from "@shared/features/bots/BotForm/useBotForm";
import {
  BOT_FORM_BOTTOM_SPACE,
  BOT_TYPE_ANALYTICS_MAPPING,
  BOT_TYPE_TO_BASE_PATH,
  DRAWER_BACKTESTING_QUERY_PARAM,
} from "features/bots/common/constants";
import { useQueryBotType } from "features/bots/common/hooks";
import { BotType, LanguageNameSpaces, WorkspaceType } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { TranslationKeys } from "@shared/common/i18n/types";
import BotFormChart from "features/bots/BotForm/BotFormChart";
import {
  useBottomNavigator,
  useDisablePageScroll,
  useScrollToTop,
  useVirtualKeyboard,
} from "utils/hooks/misc";
import { useHistory } from "react-router-dom";
import {
  ADMIN_TRADING_CONFIGURATION_PATH,
  NOT_GROUP_MEMBER_MESSAGE,
  TABS_HEADER_HEIGHT,
} from "common/constants";
import QueriesError from "common/Empty/QueriesError";
import BacktestingFormItems from "features/bots/BotForm/Backtesting/BacktestingFormItems/BacktestingFormItems";
import BacktestingValuesModifiedMessage from "features/bots/BotForm/Backtesting/BacktestingValuesModifiedMessage.tsx";
import {
  BotFormItemWrapper,
  FormAndSummaryWrapper,
  SummaryInnerWrapper,
} from "features/bots/BotForm/common/styles";
import HighlightedText from "common/Text/HighlightedText";
import { useSelector } from "react-redux";
import { selectWorkspace } from "@shared/slices/common.slice";
import { useLiveGroupSearch } from "@shared/utils/hooks/misc";
import { openMessage } from "utils/misc";
import useMeasure from "utils/hooks/useMeasure.ts";
import BotFormSummaryTabs from "features/bots/BotForm/Summaries/BotFormSummaryTabs.tsx";
import useQuery from "utils/hooks/useQuery.ts";
import MobileBotForm from "features/bots/MobileBotForm.tsx";
import { useTelegramWebAppBottomPadding } from "utils/hooks/useTelegramWebApp.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { BOT_FORM_SUMMARY_WRAPPER_ID, getBotFormTabId } from "testIds.ts";
import { usePageIntro } from "common/NewLook";
import NoAccountCardHighlightProvider from "features/bots/BotForm/common/NoAccountCardHighlightProvider";

const BotForm = ({
  practicePreset,
}: { practicePreset?: PracticePreset } = {}) => {
  const {
    botType,
    botIDs,
    botFormType,
    botUUID,
    botIdStr,
    isChannelConfiguration,
    configurationType,
    isBacktesting,
    backtestingBotId,
    isCopyToWorkspace,
    isDemoCopyPublic,
    subGroupId,
  } = useQueryBotType();
  const theme = useTheme();
  const { isNotMember, liveUpdateChannelsQuery } =
    useLiveGroupSearch(subGroupId);
  const history = useHistory();
  const query = useQuery();
  const backtestingDrawerOpen = !!query.get(DRAWER_BACKTESTING_QUERY_PARAM);
  // Onboarding's confirm mode keeps the form mounted but hidden; the page
  // (the summary card) scrolls as usual then.
  const onboardingSetup = useContext(OnboardingBotSetupContext);
  useDisablePageScroll(!onboardingSetup?.confirming);

  const getBotId = () => {
    if (botIDs?.length) return botIDs[0];
    return botIdStr ? parseInt(botIdStr) : undefined;
  };

  const botID = getBotId();

  useEffect(() => {
    if (isNotMember) {
      openMessage(NOT_GROUP_MEMBER_MESSAGE, "warning");
      const nextPath = isChannelConfiguration
        ? ADMIN_TRADING_CONFIGURATION_PATH
        : BOT_TYPE_TO_BASE_PATH[botType];
      history.push(nextPath);
    }
  }, [isNotMember]);

  if (
    (liveUpdateChannelsQuery.isLoading && !backtestingDrawerOpen) ||
    isNotMember
  ) {
    return (
      <LoadingSpinnerOverlay
        isLoading={true}
        backgroundColor={theme.colors.layoutBgColor}
        spinnerMarginTop={100}
        spinnerSize="large"
      />
    );
  }

  return (
    <BotFormProvider
      key={botType}
      botType={botType}
      botFormType={botFormType}
      botID={botID}
      botUUID={botUUID}
      botIDs={botIDs}
      isChannelConfiguration={isChannelConfiguration}
      isBacktesting={isBacktesting}
      backtestingBotId={
        backtestingBotId ? parseInt(backtestingBotId) : undefined
      }
      isCopyToWorkspace={isCopyToWorkspace}
      isDemoCopyPublic={isDemoCopyPublic}
      configurationType={configurationType}
      subGroupId={subGroupId}
    >
      <OnboardingFormScope>
        <NoAccountCardHighlightProvider>
          <InnerBotForm />
        </NoAccountCardHighlightProvider>
        {practicePreset && <PracticeBotPreset preset={practicePreset} />}
      </OnboardingFormScope>
    </BotFormProvider>
  );
};

export default BotForm;

const OnboardingFormScope = ({ children }: React.PropsWithChildren) => {
  const form = useContext(BotFormContext)!;
  const onboarding = useContext(OnboardingBotSetupContext);
  if (!onboarding) return <>{children}</>;
  return (
    <BotFormContext.Provider
      value={{ ...form, showBacktesting: false, showPublicBots: false }}
    >
      {children}
    </BotFormContext.Provider>
  );
};

const InnerBotForm = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const onboarding = useContext(OnboardingBotSetupContext);
  const appTheme = useTheme();
  const isMobile = useMedia([`(max-width: 900px)`], [true], false);
  const { analytics } = useContext(AnalyticsContext);
  const workspace = useSelector(selectWorkspace);
  const pagePadding = useMedia([`(max-width: 500px)`], [12], 20);
  const summaryTabsSmallScreenLayout = useMedia(
    [`(max-width: 1350px)`],
    [true],
    false
  );
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { isKeyboardOpen } = useVirtualKeyboard(isBottomNavigator);
  // New look (desktop): no page toolbar; the form fills the rest of the
  // canvas (tablet widths scroll the page instead).
  const isNewLookPage = appTheme.isNewLook && !isBottomNavigator && !onboarding;
  usePageIntro(isNewLookPage ? { description: "" } : {}, [isNewLookPage]);
  const { ref: fillHeightRef, height: fillHeight } = useFillHeight(
    isNewLookPage && !isMobile
  );
  const [messageMeasureRef, { height: messageHeight }] =
    useMeasure<HTMLDivElement>();
  const {
    botType,
    botFormType,
    isFetching,
    isLoading,
    isError,
    refetchErrorQueries,
    isAdvancedView,
    isBacktesting,
    backtestingValuesModified,
    isCopyToWorkspace,
    showBacktesting,
    showPublicBots,
  } = useContext(BotFormContext)!;
  const { tabsData, summaryProps, chartTab, setChartTab } = useBotForm();
  const futuresAsSpot = useFollowFuturesAsSpot();
  useScrollToTop();
  const telegramBottomPadding = useTelegramWebAppBottomPadding();
  const isBulkAction =
    botFormType === BotFormType.BULK_EDIT ||
    botFormType === BotFormType.BULK_CREATE;
  const isSignalsBot = botType === BotType.SIGNAL;
  const showBotSummaryTabs = showPublicBots || showBacktesting;
  const isChartLayout = !onboarding && !isBulkAction && !isSignalsBot;
  const showTabs = isAdvancedView;

  const getAnalyticsCreateEditOrigin = () => {
    let origin: DcaBotCreateeditOriginValueType = DcaBotCreateeditOrigin.CREATE;
    if (botFormType === BotFormType.COPY_PUBLIC) {
      origin = DcaBotCreateeditOrigin.EDIT_AND_COPY;
    } else if (botFormType === BotFormType.EDIT) {
      origin = DcaBotCreateeditOrigin.EDIT;
    } else if (botFormType === BotFormType.BULK_EDIT) {
      origin = DcaBotCreateeditOrigin.BULK_EDIT;
    } else if (botFormType === BotFormType.BULK_CREATE) {
      origin = DcaBotCreateeditOrigin.BULK_CREATE;
    }
    return origin;
  };

  const analyticsOrigin = getAnalyticsCreateEditOrigin();

  useEffect(() => {
    analytics?.botBotFormViewWeb({
      dcaBotCreateeditOrigin: analyticsOrigin,
      botType: BOT_TYPE_ANALYTICS_MAPPING[botType],
    });
  }, [analytics, analyticsOrigin, botType]);

  const getTab = (tab: Steps, displayText: string) => {
    if (isNewLookPage) {
      const hasError = tabsData.tabsWithErrors.has(tab);
      return (
        <StepLabel data-testid={getBotFormTabId(tab)} $hasError={hasError}>
          {displayText}
          {hasError && <StepErrorDot />}
        </StepLabel>
      );
    }
    if (tabsData.tabsWithErrors.has(tab)) {
      return (
        <Text
          as="div"
          data-testid={getBotFormTabId(tab)}
          type="danger"
          $size="S"
          $weight={typography.fontWeights.NORMAL}
          style={{ display: "flex", alignItems: "center" }}
        >
          {displayText}
        </Text>
      );
    }
    return <span data-testid={getBotFormTabId(tab)}>{displayText}</span>;
  };

  const tabToComponent: Record<Steps, React.JSX.Element> = useMemo(
    () => ({
      [Steps.GENERAL]: <General futuresAsSpot={futuresAsSpot} />,
      [Steps.ENTRIES]: <Entries />,
      [Steps.TAKE_PROFITS]: <TakeProfits />,
      [Steps.STOP]: <Stop />,
      [Steps.ADVANCED]: <Advanced futuresAsSpot={futuresAsSpot} />,
    }),
    [futuresAsSpot]
  );

  const summary = (
    <Summary
      {...summaryProps}
      analyticsBotOriginType={analyticsOrigin}
      isMobile={isMobile}
    />
  );

  const valuesModifiedMessage = useMemo(
    () =>
      isBacktesting &&
      backtestingValuesModified && (
        <div ref={messageMeasureRef}>
          <BacktestingValuesModifiedMessage />
        </div>
      ),
    [isBacktesting && backtestingValuesModified]
  );

  const copiedToWorkspaceMessage = useMemo(
    () =>
      isCopyToWorkspace && (
        <HighlightedText
          style={{
            padding: "8px 12px",
            margin:
              isBottomNavigator || isNewLookPage ? "0 0 12px 0" : "0 16px 12px",
          }}
          color={appTheme.colors.primaryText}
          borderColor={appTheme.colors.primaryBlue}
          bgColor={appTheme.colors.statisticsBgColor}
        >
          Copied from {workspace === WorkspaceType.DEMO ? "Live" : "Demo"}{" "}
          Trading workspace
        </HighlightedText>
      ),
    [isCopyToWorkspace, isBottomNavigator, isNewLookPage, appTheme, workspace]
  );

  const tabsItems = useMemo(() => {
    return tabsData.options.map((option) => ({
      key: option.tab,
      label: getTab(option.tab, option.displayText),
    }));
  }, [tabsData.options, tabsData.tabsWithErrors, isNewLookPage]);

  const inlineTabs = showTabs ? (
    <StyledTabs
      activeKey={tabsData.value}
      onChange={(tab: string) => tabsData.onChange(tab as Steps)}
      $isSmall={true}
      items={tabsItems}
    />
  ) : undefined;

  const chartTabToComponent = {
    [ChartTabs.CHART]: <BotFormChart isCard={true} />,
    [ChartTabs.BACKTESTING_RESULTS]: (
      <BacktestingFormItems
        style={{
          marginTop: 0,
          paddingTop: 0,
          border: "none",
          height: "100%",
          overflow: "auto",
          padding: "0 12px 12px",
        }}
      />
    ),
  };

  const chartTabsItems = Object.values(ChartTabs).map((tab) => {
    return {
      key: tab,
      label: t(CHART_TAB_TO_DISPLAY_NAME[tab]),
      children: chartTabToComponent[tab],
    };
  });

  const chartTabs = (
    <StyledChartTabs
      activeKey={chartTab}
      onChange={(tab: string) => setChartTab(tab as ChartTabs)}
      $isSmall={true}
      items={chartTabsItems}
    />
  );

  const botSummaryTabs = showBotSummaryTabs && (
    <BotFormSummaryTabs isSmallScreen={summaryTabsSmallScreenLayout} />
  );

  const getLargeChartLayout = () => {
    return (
      <ContentWrapper
        style={{
          padding: pagePadding,
        }}
      >
        <FormAndChartWrapper>
          <FormAndSummaryWrapper $isMobile={isMobile} $isChartLayout={true}>
            <BotFormItemWrapper
              $isChartLayout={true}
              $isMobile={isMobile && !isBacktesting}
              $summaryTabsSmallScreenLayout={summaryTabsSmallScreenLayout}
            >
              {inlineTabs}
              {copiedToWorkspaceMessage}
              {valuesModifiedMessage}
              {tabsData.tabsOrder.map((iterTab) => (
                <TabViewStepWrapper
                  $visible={tabsData.value === iterTab}
                  key={iterTab}
                  $isChartLayout={isChartLayout}
                  $showTabs={showTabs}
                  $messageHeight={messageHeight}
                >
                  <LoadingSpinnerOverlay
                    isLoading={isFetching}
                    backgroundColor={appTheme.colors.cardBgColor}
                  >
                    {tabToComponent[iterTab]}
                  </LoadingSpinnerOverlay>
                </TabViewStepWrapper>
              ))}
            </BotFormItemWrapper>
            {isMobile && isBacktesting && (
              <BacktestingResultsWrapper $isMobile={isMobile}>
                <BacktestingFormItems />
              </BacktestingResultsWrapper>
            )}
            <SummaryWrapper
              $isMobile={isMobile}
              $hide={isKeyboardOpen}
              $isChartLayout={true}
            >
              <SummaryInnerWrapper
                $isMobile={isMobile}
                $pagePadding={pagePadding}
                $isChartLayout={true}
                $bordered={true}
                $showBotSummaryTabs={
                  summaryTabsSmallScreenLayout && showBotSummaryTabs
                }
              >
                {summary}
              </SummaryInnerWrapper>
            </SummaryWrapper>
            {!isMobile && summaryTabsSmallScreenLayout && botSummaryTabs}
          </FormAndSummaryWrapper>
          {!isMobile && (
            <TabsViewChartWrapper $isKeyboardOpen={isKeyboardOpen}>
              {showBacktesting ? chartTabs : <BotFormChart isCard={true} />}
            </TabsViewChartWrapper>
          )}
          {!summaryTabsSmallScreenLayout && botSummaryTabs}
        </FormAndChartWrapper>
      </ContentWrapper>
    );
  };

  const getLargeSummaryLayout = () => {
    return (
      <ContentWrapper
        style={{
          padding: pagePadding,
        }}
      >
        <FormAndSummaryWrapper $isMobile={isMobile} $isChartLayout={false}>
          <FormSectionsWrapper $isBacktesting={isBacktesting}>
            <BotFormItemWrapper $isMobile={isMobile && !isBacktesting}>
              {inlineTabs}
              {copiedToWorkspaceMessage}
              {valuesModifiedMessage}
              {tabsData.tabsOrder.map((iterTab) => (
                <TabViewStepWrapper
                  $visible={tabsData.value === iterTab}
                  key={iterTab}
                  $showTabs={showTabs}
                  $messageHeight={messageHeight}
                >
                  <LoadingSpinnerOverlay
                    isLoading={isFetching}
                    backgroundColor={appTheme.colors.cardBgColor}
                  >
                    {tabToComponent[iterTab]}
                  </LoadingSpinnerOverlay>
                </TabViewStepWrapper>
              ))}
            </BotFormItemWrapper>
            {isBacktesting && (
              <BacktestingResultsWrapper $isMobile={isMobile}>
                <BacktestingFormItems />
              </BacktestingResultsWrapper>
            )}
          </FormSectionsWrapper>
          <SummaryWrapper
            data-testid={BOT_FORM_SUMMARY_WRAPPER_ID}
            $isMobile={isMobile}
            $hide={isKeyboardOpen}
          >
            <SummaryInnerWrapper
              $isMobile={isMobile}
              $pagePadding={pagePadding}
            >
              {summary}
            </SummaryInnerWrapper>
          </SummaryWrapper>
        </FormAndSummaryWrapper>
      </ContentWrapper>
    );
  };

  const getNewLookLayout = () => {
    const stepTabs = showTabs ? (
      <NewLookStepTabs
        activeKey={tabsData.value}
        onChange={(tab: string) => tabsData.onChange(tab as Steps)}
        items={tabsItems}
      />
    ) : undefined;
    const steps = tabsData.tabsOrder.map((iterTab) => (
      <NewLookStep $visible={tabsData.value === iterTab} key={iterTab}>
        <LoadingSpinnerOverlay
          isLoading={isFetching}
          backgroundColor={appTheme.colors.cardBgColor}
        >
          {tabToComponent[iterTab]}
        </LoadingSpinnerOverlay>
      </NewLookStep>
    ));
    const newLookChartTabsItems = Object.values(ChartTabs).map((tab) => ({
      key: tab,
      label: tab,
      children:
        tab === ChartTabs.CHART ? (
          <BotFormChart isCard={true} />
        ) : (
          <BacktestingFormItems
            style={{
              margin: 0,
              border: "none",
              borderRadius: 0,
              boxShadow: "none",
              height: "100%",
              overflow: "auto",
              padding: "16px 20px 20px",
            }}
          />
        ),
    }));
    const preview = isChartLayout ? (
      showBacktesting ? (
        <NewLookPreviewTabs
          activeKey={chartTab}
          onChange={(tab: string) => setChartTab(tab as ChartTabs)}
          items={newLookChartTabsItems}
        />
      ) : (
        <BotFormChart isCard={true} />
      )
    ) : undefined;
    const helpers =
      isChartLayout && showBotSummaryTabs ? (
        <BotFormSummaryTabs isNewLookPanel={true} />
      ) : undefined;
    return (
      <Summary
        {...summaryProps}
        analyticsBotOriginType={analyticsOrigin}
        isMobile={isMobile}
        renderNewLook={(slots) => (
          <NewLookBotFormLayout
            slots={slots}
            stepTabs={stepTabs}
            messages={
              <>
                {copiedToWorkspaceMessage}
                {isMobile && isBacktesting ? undefined : valuesModifiedMessage}
              </>
            }
            steps={
              <>
                {steps}
                {isMobile && isBacktesting && (
                  <BacktestingResultsWrapper $isMobile={false}>
                    {valuesModifiedMessage}
                    <BacktestingFormItems />
                  </BacktestingResultsWrapper>
                )}
                {!isChartLayout && isBacktesting && (
                  <BacktestingResultsWrapper $isMobile={false}>
                    <BacktestingFormItems />
                  </BacktestingResultsWrapper>
                )}
              </>
            }
            preview={preview}
            helpers={helpers}
            asideHasCards={isBulkAction}
            isNarrow={isMobile}
          />
        )}
      />
    );
  };

  const getContent = () => {
    if (isError)
      return <QueriesError refetchErrorQueries={refetchErrorQueries} />;
    if (isNewLookPage) return getNewLookLayout();
    if (isBottomNavigator && appTheme.isNewLook) {
      return (
        <Summary
          {...summaryProps}
          analyticsBotOriginType={analyticsOrigin}
          isMobile={isMobile}
          isNewLookPhone={true}
          renderNewLook={(slots) => (
            <NewLookMobileBotForm
              valuesModifiedMessage={valuesModifiedMessage}
              copiedToWorkspaceMessage={copiedToWorkspaceMessage}
              tabsItems={tabsItems}
              tabToComponent={tabToComponent}
              summary={slots.footer}
              advancedSwitch={slots.advancedSwitch}
              notice={slots.notice}
            />
          )}
        />
      );
    }
    if (isBottomNavigator) {
      return (
        <MobileBotForm
          valuesModifiedMessage={valuesModifiedMessage}
          copiedToWorkspaceMessage={copiedToWorkspaceMessage}
          tabsItems={tabsItems}
          tabToComponent={tabToComponent}
          summary={summary}
        />
      );
    }
    if (!isChartLayout) return getLargeSummaryLayout();
    return getLargeChartLayout();
  };

  // The new-look phone form's action row keeps clear of Telegram's inset.
  const isNewLookPhoneForm =
    isBottomNavigator && appTheme.isNewLook && !isNewLookPage;
  const style = useMemo(() => {
    if (IS_TELEGRAM_WEB_APP && !isNewLookPhoneForm) {
      return { marginBottom: telegramBottomPadding };
    }
  }, [telegramBottomPadding, isNewLookPhoneForm]);

  return (
    <Wrapper
      style={fillHeight ? { ...style, height: fillHeight } : style}
      ref={fillHeightRef}
    >
      <LoadingSpinnerOverlay
        isLoading={isLoading}
        backgroundColor={appTheme.colors.layoutBgColor}
        hideContentWhileLoading={true}
        spinnerMarginTop={isBottomNavigator ? undefined : 100}
        spinnerSize="large"
        wrapStyle={{ minHeight: "100%", height: 1 }}
      >
        {getContent()}
      </LoadingSpinnerOverlay>
    </Wrapper>
  );
};

const CHART_TAB_TO_DISPLAY_NAME: Record<
  ChartTabs,
  TranslationKeys<LanguageNameSpaces.BOTS>
> = {
  [ChartTabs.CHART]: "tabs.chart",
  [ChartTabs.BACKTESTING_RESULTS]: "tabs.backtesting_results",
};
const Wrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;
const ContentWrapper = styled.div`
  height: 100%;
  margin: 0 auto;
  position: relative;
  overflow-y: auto;
`;

const SummaryWrapper = styled.div<{
  $isMobile?: boolean;
  $isBottomNavigator?: boolean;
  $bottomNavigatorHeight?: number;
  $hide?: boolean;
  $isChartLayout?: boolean;
}>`
  ${(p) =>
    p.$isMobile &&
    `
  position: fixed;
  bottom: ${p.$isBottomNavigator ? p.$bottomNavigatorHeight : 0}px;
  width: 100%;
  z-index: 999;
  `};
  ${(p) =>
    p.$hide &&
    `
    display: none;
  `};
  ${(p) =>
    !p.$isChartLayout &&
    `
    flex: 0 0 400px;
    height: fit-content;
  `};
`;

const FormAndChartWrapper = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  column-gap: 20px;
`;

const TabViewStepWrapper = styled.div<{
  $visible: boolean;
  $isChartLayout?: boolean;
  $showTabs?: boolean;
  $messageHeight?: number;
}>`
  height: ${(p) =>
    p.$showTabs
      ? `calc(100% - ${TABS_HEADER_HEIGHT + (p.$messageHeight ?? 0)}px)`
      : "100%"};
  overflow-y: auto;
  position: relative;
  ${(p) =>
    !p.$visible &&
    `
    display: none;
    opacity: 0;
    pointer-events: none;
  `};
  padding: ${(p) => (p.$isChartLayout ? 12 : 16)}px;
  ${(p) =>
    p.$showTabs &&
    `
    padding-top: 0;
  `};
  @media (max-width: 600px) {
    padding: 12px;
  }
`;
const TabsViewChartWrapper = styled.div<{
  $isKeyboardOpen: boolean;
}>`
  background: ${(p) => p.theme.colors.cardBgColor};
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  height: calc(100% - ${BOT_FORM_BOTTOM_SPACE}px);
  padding: 8px 0 4px 0;
  overflow-y: hidden;
  ${(p) =>
    p.$isKeyboardOpen
      ? `
    position: relative;
  `
      : `
    flex: 0 0 400px;
`}
  flex: 1;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${formPanel};
      padding: 0;
      overflow: hidden;
    `};
`;
const StyledTabs = styled(Tabs)`
  .ant-tabs-tab {
    padding-top: 0;
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin: 0 0 0 18px;
  }

  &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
    padding: 12px 12px 0;
  }

  .ant-tabs-content-holder {
    overflow-y: auto;
  }

  &.ant-tabs {
    height: ${TABS_HEADER_HEIGHT}px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      ${panelTabs};
    `};
`;
const FormSectionsWrapper = styled.div<{
  $isBacktesting?: boolean;
  $isMobile?: boolean;
}>`
  display: ${(p) => (p.$isBacktesting ? "grid" : "flex")};
  width: 100%;
  height: fit-content;
`;
const StyledChartTabs = styled(Tabs)`
  height: 100%;

  .ant-tabs-content,
  .ant-tabs-tabpane {
    height: 100%;
    overflow-y: auto;
  }

  &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
    padding: 4px 12px 0;
  }

  .ant-tabs-tab {
    padding-top: 0;
  }
  ${(p) => p.theme.isNewLook && panelTabs};
`;
const BacktestingResultsWrapper = styled.div<{ $isMobile?: boolean }>`
  display: grid;
  ${(p) =>
    p.$isMobile &&
    `
    padding-bottom: 160px;
  `};
`;

/* New look */
const NewLookStepTabs = styled(Tabs)`
  ${panelTabs};
  .ant-tabs-content-holder {
    display: none;
  }
`;
const StepLabel = styled.span<{ $hasError?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  ${(p) =>
    p.$hasError &&
    css`
      color: ${p.theme.colors.red};
    `};
`;
const StepErrorDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${(p) => p.theme.colors.red};
`;
const NewLookStep = styled.div<{ $visible: boolean }>`
  position: relative;
  ${(p) =>
    !p.$visible &&
    css`
      display: none;
    `};
`;
const NewLookPreviewTabs = styled(Tabs)`
  ${panelTabs};
  height: 100%;
  display: flex;
  flex-direction: column;
  &.ant-tabs .ant-tabs-content-holder {
    flex: 1 1 auto;
    min-height: 0;
  }
  &.ant-tabs .ant-tabs-content,
  &.ant-tabs .ant-tabs-tabpane {
    height: 100%;
  }
  &.ant-tabs .ant-tabs-tabpane {
    overflow-y: auto;
  }
`;
